import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import '../communications.css';

function Communications() {
  const [communicationsData, setCommunicationsData] = useState({ stats: null, dialogs: [] });
  const [selectedDialog, setSelectedDialog] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [managerInfo, setManagerInfo] = useState(null);
  const [showDialogsOnMobile, setShowDialogsOnMobile] = useState(false);
  const [lastMessageTime, setLastMessageTime] = useState(null);
  const [lastMessageId, setLastMessageId] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all'); // all, personal, group
  const messagesContainerRef = useRef(null);

  // Функция для автоматической прокрутки вниз
  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, []);

  // Загрузка данных коммуникационного центра
  const loadCommunicationsData = useCallback(async () => {
    try {
      const data = await api.getCommunicationsData();
      setCommunicationsData(data);
    } catch (error) {
      console.error('Error loading communications data:', error);
      toast.error('Ошибка загрузки данных коммуникационного центра');
    } finally {
      setLoading(false);
    }
  }, []);

  // Загрузка истории сообщений
  const loadMessages = useCallback(async (dialogId, forceScrollToBottom = false) => {
    try {
      const data = await api.getDialogHistory(dialogId, 50);
      const loadedMessages = data.messages || [];

      // Сортируем сообщения по времени на всякий случай
      loadedMessages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      setMessages(loadedMessages);
      setManagerInfo(data.manager);

      // Сохраняем время и ID последнего сообщения для отслеживания изменений
      if (loadedMessages.length > 0) {
        const latestMessage = loadedMessages[loadedMessages.length - 1];
        setLastMessageTime(latestMessage.created_at);
        setLastMessageId(latestMessage.id);
      }

      // Прокручиваем вниз при загрузке новых сообщений
      if (forceScrollToBottom) {
        setTimeout(scrollToBottom, 100);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Ошибка загрузки сообщений');
    }
  }, [scrollToBottom]);

  // Реал-тайм опрос для обновления диалогов и сообщений
  useEffect(() => {
    // Запускаем polling только если есть выбранный диалог
    if (!selectedDialog) return;

    // Функция для проверки новых сообщений
    const pollForNewMessages = async () => {
      try {
        // Получаем последние сообщения для текущего диалога
        const messagesData = await api.getDialogHistory(selectedDialog.id, 50);

        if (messagesData.messages && messagesData.messages.length > 0) {
          const latestMessage = messagesData.messages[messagesData.messages.length - 1];

          // Проверяем есть ли новые сообщения по времени или ID
          const hasNewMessages = !lastMessageTime ||
            !lastMessageId ||
            latestMessage.created_at !== lastMessageTime ||
            latestMessage.id !== lastMessageId;

          if (hasNewMessages) {
            // Добавляем только новые сообщения в конец списка
            setMessages(prevMessages => {
              // Находим сообщения, которые еще не в списке
              const existingIds = new Set(prevMessages.map(m => m.id));
              const newMessages = messagesData.messages.filter(m => !existingIds.has(m.id));

              if (newMessages.length > 0) {
                // Сортируем новые сообщения по времени
                newMessages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

                // Плавно прокручиваем вниз при добавлении новых сообщений
                setTimeout(scrollToBottom, 50);
              }

              return [...prevMessages, ...newMessages];
            });

            setLastMessageTime(latestMessage.created_at);
            setLastMessageId(latestMessage.id);
            setManagerInfo(messagesData.manager);
          }
        }

        // Обновляем список диалогов для показа новых сообщений в превью
        const communicationsData = await api.getCommunicationsData();
        setCommunicationsData(communicationsData);
      } catch (error) {
        console.error('Error polling for new messages:', error);
      }
    };

    // Запускаем polling каждые 2 секунды
    const pollingInterval = setInterval(pollForNewMessages, 2000);

    // Первая проверка сразу
    pollForNewMessages();

    return () => {
      clearInterval(pollingInterval);
    };
  }, [selectedDialog, lastMessageTime, lastMessageId, scrollToBottom]);

  // Восстановление состояния при монтировании
  useEffect(() => {
    const savedDialogId = sessionStorage.getItem('selectedDialogId');

    // Сначала загружаем данные
    loadCommunicationsData();
  }, [loadCommunicationsData]);

  // Отдельный useEffect для восстановления диалога после загрузки
  useEffect(() => {
    if (communicationsData.dialogs.length > 0) {
      const savedDialogId = sessionStorage.getItem('selectedDialogId');
      if (savedDialogId) {
        const savedDialog = communicationsData.dialogs.find(d => d.id.toString() === savedDialogId);
        if (savedDialog) {
          setSelectedDialog(savedDialog);
          loadMessages(savedDialog.id, false); // Не прокручиваем, восстанавливаем позицию
        } else {
          // Если диалог не найден, очищаем sessionStorage
          sessionStorage.removeItem('selectedDialogId');
          sessionStorage.removeItem('messagesScrollPosition');
        }
      }
    }
  }, [communicationsData.dialogs, loadMessages]);

  // Восстановление позиции скролла (упрощенная версия)
  useEffect(() => {
    if (messagesContainerRef.current && messages.length > 0) {
      const container = messagesContainerRef.current;

      // Восстанавливаем сохраненную позицию если есть
      const savedPosition = sessionStorage.getItem('messagesScrollPosition');
      if (savedPosition && selectedDialog) {
        const pos = parseInt(savedPosition);
        // Проверяем что позиция все еще актуальна
        if (pos > 0 && pos < container.scrollHeight) {
          container.scrollTop = pos;
        }
      }

      // Добавляем listener для сохранения скролла
      const handleScroll = () => {
        const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 50;
        if (!isAtBottom) {
          sessionStorage.setItem('messagesScrollPosition', container.scrollTop.toString());
        } else {
          sessionStorage.removeItem('messagesScrollPosition');
        }
      };

      container.addEventListener('scroll', handleScroll);

      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [messages, selectedDialog]);

  // Автоматическая прокрутка вниз при смене диалога или при новых сообщениях
  useEffect(() => {
    if (selectedDialog && messagesContainerRef.current) {
      // Небольшая задержка чтобы конт успел отрендериться
      setTimeout(scrollToBottom, 150);
    }
  }, [selectedDialog, messages.length, scrollToBottom]);

  // Выбор диалога
  const handleDialogSelect = (dialog) => {
    setSelectedDialog(dialog);
    loadMessages(dialog.id, true); // Всегда прокручиваем вниз при выборе диалога

    // Сохраняем выбранный диалог
    sessionStorage.setItem('selectedDialogId', dialog.id);

    // Очищаем позицию скролла при смене диалога
    sessionStorage.removeItem('messagesScrollPosition');

    // На мобильных скрываем диалоги после выбора
    if (window.innerWidth <= 768) {
      setShowDialogsOnMobile(false);
    }
  };

  // Фильтрация диалогов
  const filteredDialogs = communicationsData.dialogs.filter(dialog => {
    const matchesSearch = dialog.user_name.toLowerCase().includes(searchQuery.toLowerCase());

    if (activeFilter === 'all') return matchesSearch;
    if (activeFilter === 'personal') return dialog.category === 'personal' && matchesSearch;
    if (activeFilter === 'group') return dialog.category === 'group' && matchesSearch;

    return matchesSearch;
  });

  // Форматирование времени
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  const formatFullDateTime = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }) + ' ' +
             date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    }
  };

  // Получение стилей для сообщения
  const getMessageStyle = (senderType) => {
    switch(senderType) {
      case 'user':
        return {
          container: 'message-bubble message-incoming',
          bubble: 'message-bubble-user',
          label: 'Пользователь',
          color: '#3b82f6'
        };
      case 'manager':
        return {
          container: 'message-bubble message-outgoing',
          bubble: 'message-bubble-manager',
          label: managerInfo?.name || 'Менеджер',
          color: '#22c55e'
        };
      case 'bot':
        return {
          container: 'message-bubble message-outgoing',
          bubble: 'message-bubble-bot',
          label: 'Бот',
          color: '#6b7280'
        };
      default:
        return {
          container: 'message-bubble message-outgoing',
          bubble: 'message-bubble-bot',
          label: 'Система',
          color: '#6b7280'
        };
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="communications-page">
      <div className="communications-container">
        {/* Левая панель - список диалогов */}
        <div className={`dialogs-sidebar ${showDialogsOnMobile ? 'mobile-show' : 'mobile-hide'}`}>
          <div className="dialogs-sidebar-header">
            <h3>Диалоги</h3>
          </div>

          {/* Фильтры */}
          <div className="filters-container">
            <div className="filter-buttons">
              <button
                className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
                onClick={() => setActiveFilter('all')}
              >
                Все чаты
                <span className="filter-count">{communicationsData.stats?.total_chats || 0}</span>
              </button>
              <button
                className={`filter-btn ${activeFilter === 'personal' ? 'active' : ''}`}
                onClick={() => setActiveFilter('personal')}
              >
                С ботом
                <span className="filter-count">{communicationsData.stats?.personal_chats?.total || 0}</span>
              </button>
              <button
                className={`filter-btn ${activeFilter === 'group' ? 'active' : ''}`}
                onClick={() => setActiveFilter('group')}
              >
                Группы
                <span className="filter-count">{communicationsData.stats?.group_chats?.total || 0}</span>
              </button>
            </div>
          </div>

          <div className="search-container">
            <input
              type="text"
              placeholder="Поиск по имени..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="dialogs-list">
            {filteredDialogs.length === 0 ? (
              <div className="no-dialogs">Нет диалогов</div>
            ) : (
              filteredDialogs.map((dialog) => (
                <div
                  key={dialog.id}
                  data-dialog-id={dialog.id}
                  className={`dialog-item ${selectedDialog?.id === dialog.id ? 'active' : ''}`}
                  onClick={() => handleDialogSelect(dialog)}
                >
                  <div className="dialog-avatar">
                    {dialog.category === 'personal' ? 'L' : 'G'}
                  </div>
                  <div className="dialog-content">
                    <div className="dialog-header">
                      <span className="dialog-name" title={dialog.user_name}>
                        {dialog.user_name}
                      </span>
                      {dialog.last_message_time && (
                        <span className="dialog-time">
                          {formatTime(dialog.last_message_time)}
                        </span>
                      )}
                    </div>
                    <div className="dialog-preview">
                      {dialog.last_message && dialog.last_message.trim() !== ''
                        ? dialog.last_message.length > 50
                          ? dialog.last_message.substring(0, 50) + '...'
                          : dialog.last_message
                        : 'Нет сообщений'
                      }
                    </div>
                    <div className="dialog-meta">
                      <span className="chat-type-indicator">
                        {dialog.category === 'personal' ? 'С ботом' : 'Групповой чат'}
                      </span>
                      {dialog.has_manager && (
                        <span className="manager-badge">
                          {dialog.manager_name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Правая панель - чат */}
        <div className="chat-window">
          {selectedDialog ? (
            <>
              <div className="chat-header">
                <div className="chat-header-left">
                  <button
                    className="mobile-dialogs-toggle"
                    onClick={() => setShowDialogsOnMobile(!showDialogsOnMobile)}
                    title="Показать диалоги"
                  >
                    ☰
                  </button>
                  <div className="chat-header-avatar">
                    {selectedDialog.user_name.charAt(0).toUpperCase()}
                  </div>
                  <div className="chat-header-info">
                    <div className="chat-header-name">{selectedDialog.user_name}</div>
                    <div className="chat-header-status">
                      {managerInfo ? `Менеджер: ${managerInfo.name}` : 'Диалог с ботом'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="messages-container" ref={messagesContainerRef}>
                {messages.length === 0 ? (
                  <div className="no-messages">Нет сообщений в этом диалоге</div>
                ) : (
                  messages.map((message) => {
                    const style = getMessageStyle(message.sender_type);

                    // Пропускаем сообщения без текста
                    if (!message.message || message.message.trim() === '') {
                      return null;
                    }

                    // Показываем все сообщения - фильтрация убрана

                    return (
                      <div key={message.id} className={style.container}>
                        <div className="message-avatar" style={{ backgroundColor: style.color }}>
                          {style.label.charAt(0).toUpperCase()}
                        </div>
                        <div className="message-content">
                          <div className="message-sender">
                            {style.label}
                          </div>
                          <div className={`message-text ${style.bubble}`}>
                            {message.message}
                          </div>
                          <div className="message-time">
                            {formatFullDateTime(message.created_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <div className="no-dialog-selected">
              <div className="placeholder-icon">💬</div>
              <h3>Выберите диалог</h3>
              <p>Выберите диалог из списка, чтобы посмотреть историю сообщений</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Communications;
